import React, { useState, useEffect } from "react";
import { useBoolean } from "@fluentui/react-hooks";
import { useNavigate } from "react-router-dom";
import i18n from "i18next";
import { IUserProduct } from "../../Models/IUserProduct";
import {
  CounterBadge,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  MessageBar,
  Tab,
  TabList,
  Text,
} from "@fluentui/react-components";
import { IUserProductsListDto } from "../../Models/IUserProductsListDto";
import {
  ChannelShareRegular,
  PersonWrenchRegular,
} from "@fluentui/react-icons";
import { UserProductsListColumns } from "./UserProductsListColumns";
import { StackShim } from "@fluentui/react-migration-v8-v9";
import { IUserProductMigrationRequestDto } from "../../Models/IUserProductmigrationRequestDto";
import { baseUrl } from "../../helpers/constants";
import { fetchWrapper } from "../../helpers/fetchwrapper";
import { UserProductMigrationListColumns } from "./UserProductMigrationListColumns";
import LuebPagedDataGrid from "../../Controls/LuebPagedDataGrid";
import AddToBedarfDialog from "../Account/Bedarfe/AddToBedarfDialog";
import DeleteUserProductDialog from "./DeleteUserProduct";
import FreigabeDialog from "../FreigabeDialog/FreigabeDialog";

// Fluent UI Imports

interface IUserProductsListProps {
  isEditModeOn: boolean;
  userProductsListDto: IUserProductsListDto;
  textFilter: string;
  isPartnerLounge: boolean;
  foundByToProductList: { userProductGuid: string; foundBy: string[] }[];
  onRefresh(): void;
  onPageChanged(): void;
}

function UserProductsList(props: IUserProductsListProps) {
  const [overtakeProducts, setOvertakeProducts] = useState<
    IUserProductMigrationRequestDto[]
  >([]);
  const [selectedTab, setSelectedTab] = useState<string>("ownProducts");
  const navigate = useNavigate();

  const isToolbox = window.location.pathname
    .toLowerCase()
    .includes("/mytoolbox");
  const [addToBedarf, setAddToBedarf] = useState<IUserProduct | undefined>(
    undefined,
  );
  const [deleteProduct, setDeleteProduct] = useState<IUserProduct | undefined>(
    undefined,
  );
  const [freigabeProduct, setFreigabeProduct] = useState<
    IUserProduct | undefined
  >(undefined);

  const columns = UserProductsListColumns({
    onSelectProduct: (
      guid: string,
      action: "delete" | "freigabe" | "openDetails" | "addToBedarf",
    ) => {
      var lists = props.userProductsListDto as IUserProductsListDto;
      var selectedProductObject =
        lists.ownProductslist.find((x) => x.userProduktGuid == guid) ??
        lists.sharedProductslist.find((x) => x.userProduktGuid == guid);
      switch (action) {
        case "openDetails":
          navigate(
            `/${isToolbox ? "mytoolbox" : "partnerlounge"}?guid=${guid}`,
          );
          break;
        case "addToBedarf":
          setAddToBedarf(selectedProductObject);
          break;
        case "delete":
          setDeleteProduct(selectedProductObject);
          break;
        case "freigabe":
          setFreigabeProduct(selectedProductObject);
          break;
      }
    },
    isPartnerLounge: props.isPartnerLounge ?? false,
    isEditModeOn: props.isEditModeOn ?? false,
    textFilter: props.textFilter,
    onRefresh: () => {
      props.onRefresh();
    },
    foundByToProductList: props.foundByToProductList,
  });

  const ownProducts = props.userProductsListDto.ownProductslist;
  const sharedProducts = props.userProductsListDto.sharedProductslist;

  const overtakeColumns = UserProductMigrationListColumns({
    textFilter: props.textFilter,
    onRefresh: () => {
      getOvertakeProductRequests();

      props.onRefresh();
    },
  });

  const getOvertakeProductRequests = () => {
    fetchWrapper
      .getWithAuth(`${baseUrl}/api/UserProduct/GetOvertakeProductRequests`)
      .then((result: IUserProductMigrationRequestDto[]) => {
        setOvertakeProducts(result);
      });
  };

  return (
    <div>
      {addToBedarf && (
        <AddToBedarfDialog
          onDismiss={() => setAddToBedarf(undefined)}
          addUserBedarfPosition={{
            kundenReferenz: addToBedarf.kundenProdukttext,
            artikel: undefined,
            produkt: addToBedarf.seriennummer,
            benennung: i18n.language.includes("de")
              ? addToBedarf.benennungDE
              : addToBedarf.benennungEN,
            menge: 1,
            reference: undefined,
          }}
        />
      )}
      <DeleteUserProductDialog
        userProduct={deleteProduct}
        isDialogOpen={deleteProduct != undefined}
        onDismiss={() => setDeleteProduct(undefined)}
      />
      <FreigabeDialog
        onDismiss={() => {
          setFreigabeProduct(undefined);
        }}
        onSave={() => {
          setFreigabeProduct(undefined);

          props.onRefresh();
        }}
        isDialogOpen={freigabeProduct != undefined}
        objectGuid={freigabeProduct?.userProduktGuid ?? ""}
        objectType="UserProduct"
        referenceGuid=""
      />
      {(ownProducts || sharedProducts) && (
        <>
          <TabList
            selectedValue={selectedTab}
            onTabSelect={(ev, data: any) => {
              if (data && data.value) {
                setSelectedTab(data.value);
              }
            }}
          >
            <Tab icon={<PersonWrenchRegular />} value="ownProducts">
              <StackShim
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: 5 }}
              >
                <Text>{i18n.t<string>("ownProducts")}</Text>

                <CounterBadge
                  count={props.userProductsListDto.ownProductslist.length}
                ></CounterBadge>
              </StackShim>
            </Tab>

            {props.userProductsListDto.sharedProductslist.length > 0 && (
              <Tab icon={<ChannelShareRegular />} value="sharedProducts">
                <StackShim
                  horizontal
                  verticalAlign="center"
                  tokens={{ childrenGap: 5 }}
                >
                  <Text>{i18n.t<string>("sharedProducts")}</Text>
                  <CounterBadge
                    count={props.userProductsListDto.sharedProductslist.length}
                  ></CounterBadge>
                </StackShim>
              </Tab>
            )}

            {overtakeProducts.length > 0 && (
              <Tab icon={<ChannelShareRegular />} value="overtakeRequests">
                <StackShim
                  horizontal
                  verticalAlign="center"
                  tokens={{ childrenGap: 5 }}
                >
                  <Text>{i18n.t<string>("overtakeRequests")}</Text>
                  <CounterBadge count={overtakeProducts.length}></CounterBadge>
                </StackShim>
              </Tab>
            )}
          </TabList>

          <div className="lueb-InnerContentBlockPadding2px">
            <>
              {selectedTab != "overtakeRequests" && (
                <>
                  <LuebPagedDataGrid
                    columns={columns ?? []}
                    items={
                      selectedTab == "ownProducts"
                        ? ownProducts
                        : sharedProducts
                    }
                    sortable
                    resizableColumns={true}
                    onPageChanged={(page) => {
                      props.onPageChanged();
                    }}
                    columnSizingOptions={{
                      vorschauPic: {
                        minWidth: 60,
                        defaultWidth: 60,
                      },
                      systemId: {
                        defaultWidth: 90,
                        minWidth: 70,
                        idealWidth: 90,
                      },
                      benennung: {
                        defaultWidth: 200,
                        minWidth: 140,
                        idealWidth: 200,
                      },
                      actions: {
                        defaultWidth: 90,
                        minWidth: 70,
                        idealWidth: 90,
                      },
                      userProductNumber: {
                        defaultWidth: 200,
                        minWidth: 160,
                        idealWidth: 200,
                      },
                      userProductText: {
                        defaultWidth: 310,
                        minWidth: 160,
                        idealWidth: 310,
                      },
                      updatedOn: {
                        defaultWidth: 90,
                        minWidth: 70,
                        idealWidth: 90,
                      },
                    }}
                  />

                  {selectedTab == "ownProducts" && ownProducts.length == 0 && (
                    <MessageBar style={{ marginTop: "5px" }} intent="info">
                      {i18n.t<string>("noItemsFound")}
                    </MessageBar>
                  )}

                  {selectedTab == "sharedProducts" &&
                    sharedProducts.length == 0 && (
                      <MessageBar style={{ marginTop: "5px" }} intent="info">
                        {i18n.t<string>("noItemsFound")}
                      </MessageBar>
                    )}
                </>
              )}

              {selectedTab == "overtakeRequests" && (
                <DataGrid
                  columns={overtakeColumns}
                  items={overtakeProducts}
                  sortable
                >
                  <DataGridHeader>
                    <DataGridRow>
                      {({ renderHeaderCell }) => (
                        <DataGridHeaderCell>
                          {renderHeaderCell()}
                        </DataGridHeaderCell>
                      )}
                    </DataGridRow>
                  </DataGridHeader>
                  <DataGridBody<IUserProduct>>
                    {({ item, rowId }) => (
                      <DataGridRow<IUserProduct> key={rowId}>
                        {({ renderCell, columnId }) => (
                          <DataGridCell>{renderCell(item)}</DataGridCell>
                        )}
                      </DataGridRow>
                    )}
                  </DataGridBody>
                </DataGrid>
              )}
            </>
          </div>
        </>
      )}
    </div>
  );
}

export default UserProductsList;
